import type { PDFPageProxy } from 'pdfjs-dist';

type PdfTextItem = {
    str: string;
    transform: number[];
    width: number;
    height: number;
};

export type PdfTextRegion = {
    id: string;
    page: number;
    /** PDF points, bottom-left origin (FPDF/FPDI) */
    x: number;
    y: number;
    w: number;
    h: number;
};

/** @deprecated Use PdfTextRegion */
export type PdfTextLine = PdfTextRegion;

const LINE_Y_TOLERANCE_PT = 3;
const PADDING_PT = 2;

type LineAccumulator = {
    items: PdfTextItem[];
    minX: number;
    maxX: number;
    minY: number;
    maxY: number;
    baselineY: number;
};

function isTextItem(item: unknown): item is PdfTextItem {
    return (
        typeof item === 'object' &&
        item !== null &&
        'str' in item &&
        typeof (item as PdfTextItem).str === 'string'
    );
}

function itemMetrics(item: PdfTextItem): {
    x: number;
    y: number;
    width: number;
    fontHeight: number;
} {
    const tx = item.transform;
    const fontHeight = Math.hypot(tx[2], tx[3]) || item.height || 12;
    const width = item.width || fontHeight * item.str.length * 0.5;

    return {
        x: tx[4],
        y: tx[5],
        width,
        fontHeight,
    };
}

export function itemToRegion(
    item: PdfTextItem,
    id: string,
    pageNum: number,
): PdfTextRegion {
    const { x, y, width, fontHeight } = itemMetrics(item);
    const bottom = y - PADDING_PT;
    const top = y + fontHeight + PADDING_PT;

    return {
        id,
        page: pageNum,
        x: Math.max(0, x - PADDING_PT),
        y: Math.max(0, bottom),
        w: Math.max(1, width + PADDING_PT * 2),
        h: Math.max(1, top - bottom),
    };
}

/**
 * Cluster text items into lines by baseline Y (PDF user space).
 */
export function clusterTextItemsIntoLines(
    items: PdfTextItem[],
    pageNum: number,
): PdfTextRegion[] {
    const lines: LineAccumulator[] = [];

    for (const item of items) {
        if (!item.str.trim()) {
            continue;
        }

        const { x, y, width, fontHeight } = itemMetrics(item);
        const itemMinX = x;
        const itemMaxX = x + width;
        const itemMinY = y;
        const itemMaxY = y + fontHeight;

        let matched: LineAccumulator | null = null;

        for (const line of lines) {
            if (Math.abs(line.baselineY - y) <= LINE_Y_TOLERANCE_PT) {
                matched = line;
                break;
            }
        }

        if (matched) {
            matched.items.push(item);
            matched.minX = Math.min(matched.minX, itemMinX);
            matched.maxX = Math.max(matched.maxX, itemMaxX);
            matched.minY = Math.min(matched.minY, itemMinY);
            matched.maxY = Math.max(matched.maxY, itemMaxY);
        } else {
            lines.push({
                items: [item],
                minX: itemMinX,
                maxX: itemMaxX,
                minY: itemMinY,
                maxY: itemMaxY,
                baselineY: y,
            });
        }
    }

    lines.sort((a, b) => b.baselineY - a.baselineY);

    return lines.map((line, index) => {
        const bottom = line.minY - PADDING_PT;
        const top = line.maxY + PADDING_PT;

        return {
            id: `${pageNum}:l${index}`,
            page: pageNum,
            x: Math.max(0, line.minX - PADDING_PT),
            y: Math.max(0, bottom),
            w: Math.max(1, line.maxX - line.minX + PADDING_PT * 2),
            h: Math.max(1, top - bottom),
        };
    });
}

/** Split one PDF.js text run into word boxes (items are often whole lines). */
export function itemToWordRegions(
    item: PdfTextItem,
    pageNum: number,
    itemIndex: number,
): PdfTextRegion[] {
    const str = item.str;

    if (!str.trim()) {
        return [];
    }

    const { x: x0, y, width: totalWidth, fontHeight } = itemMetrics(item);
    const charW = totalWidth / Math.max(1, str.length);
    const regions: PdfTextRegion[] = [];
    const wordRe = /\S+/g;
    let match: RegExpExecArray | null;
    let wordIndex = 0;

    while ((match = wordRe.exec(str)) !== null) {
        const start = match.index;
        const len = match[0].length;
        const wx = x0 + start * charW;
        const ww = Math.max(1, len * charW);
        const bottom = y - PADDING_PT;
        const top = y + fontHeight + PADDING_PT;

        regions.push({
            id: `${pageNum}:w${itemIndex}:${wordIndex}`,
            page: pageNum,
            x: Math.max(0, wx - PADDING_PT),
            y: Math.max(0, bottom),
            w: Math.max(1, ww + PADDING_PT * 2),
            h: Math.max(1, top - bottom),
        });
        wordIndex++;
    }

    return regions;
}

/** One selectable region per word (splits PDF.js line runs on whitespace). */
export function textItemsToWordRegions(
    items: PdfTextItem[],
    pageNum: number,
): PdfTextRegion[] {
    const regions: PdfTextRegion[] = [];

    items.forEach((item, index) => {
        regions.push(...itemToWordRegions(item, pageNum, index));
    });

    return sortRegionsReadingOrder(regions);
}

/** Per-character regions within each text item. */
export function itemToCharacterRegions(
    item: PdfTextItem,
    pageNum: number,
    itemIndex: number,
): PdfTextRegion[] {
    const str = item.str;

    if (!str) {
        return [];
    }

    const { x: x0, y, width: totalWidth, fontHeight } = itemMetrics(item);
    const charW = totalWidth / str.length;
    const regions: PdfTextRegion[] = [];

    for (let i = 0; i < str.length; i++) {
        const ch = str[i];

        if (ch === ' ' || ch === '\t' || ch === '\n' || ch === '\r') {
            continue;
        }

        const cx = x0 + i * charW;
        const bottom = y - 1;
        const top = y + fontHeight + 1;

        regions.push({
            id: `${pageNum}:c${itemIndex}:${i}`,
            page: pageNum,
            x: Math.max(0, cx - 0.5),
            y: Math.max(0, bottom),
            w: Math.max(1, charW + 1),
            h: Math.max(1, top - bottom),
        });
    }

    return regions;
}

export function textItemsToCharacterRegions(
    items: PdfTextItem[],
    pageNum: number,
): PdfTextRegion[] {
    const regions: PdfTextRegion[] = [];

    items.forEach((item, index) => {
        regions.push(...itemToCharacterRegions(item, pageNum, index));
    });

    return sortRegionsReadingOrder(regions);
}

/** Top-to-bottom, left-to-right (PDF user space). */
export function sortRegionsReadingOrder(
    regions: PdfTextRegion[],
): PdfTextRegion[] {
    return [...regions].sort((a, b) => {
        const yDiff = b.y - a.y;

        if (Math.abs(yDiff) > 1) {
            return yDiff;
        }

        return a.x - b.x;
    });
}

export async function extractTextItems(
    page: PDFPageProxy,
): Promise<PdfTextItem[]> {
    const textContent = await page.getTextContent();

    return textContent.items.filter(isTextItem);
}

export async function extractTextLinesFromPage(
    page: PDFPageProxy,
    pageNum: number,
): Promise<PdfTextRegion[]> {
    const items = await extractTextItems(page);

    return clusterTextItemsIntoLines(items, pageNum);
}

export async function extractTextWordsFromPage(
    page: PDFPageProxy,
    pageNum: number,
): Promise<PdfTextRegion[]> {
    const items = await extractTextItems(page);

    return textItemsToWordRegions(items, pageNum);
}

export async function extractTextCharactersFromPage(
    page: PDFPageProxy,
    pageNum: number,
): Promise<PdfTextRegion[]> {
    const items = await extractTextItems(page);

    return textItemsToCharacterRegions(items, pageNum);
}

/** Map PDF-space rect to canvas pixels (top-left origin). */
export function pdfRectToCanvas(
    rect: Pick<PdfTextRegion, 'x' | 'y' | 'w' | 'h'>,
    pageWidthPt: number,
    pageHeightPt: number,
    canvasWidth: number,
    canvasHeight: number,
): { x: number; y: number; w: number; h: number } {
    const scaleX = canvasWidth / pageWidthPt;
    const scaleY = canvasHeight / pageHeightPt;
    const topPt = pageHeightPt - rect.y - rect.h;

    return {
        x: rect.x * scaleX,
        y: topPt * scaleY,
        w: rect.w * scaleX,
        h: rect.h * scaleY,
    };
}

/** Map canvas pixels (top-left origin) to PDF-space rect (bottom-left origin). */
export function canvasRectToPdf(
    canvas: { x: number; y: number; w: number; h: number },
    pageWidthPt: number,
    pageHeightPt: number,
    canvasWidth: number,
    canvasHeight: number,
): Pick<PdfTextRegion, 'x' | 'y' | 'w' | 'h'> {
    const scaleX = pageWidthPt / canvasWidth;
    const scaleY = pageHeightPt / canvasHeight;
    const x = canvas.x * scaleX;
    const w = Math.max(1, canvas.w * scaleX);
    const h = Math.max(1, canvas.h * scaleY);
    const topPt = canvas.y * scaleY;
    const y = Math.max(0, pageHeightPt - topPt - h);

    return { x, y, w, h };
}

export function hitTestRegion(
    pt: { x: number; y: number },
    region: Pick<PdfTextRegion, 'x' | 'y' | 'w' | 'h'>,
    pageWidthPt: number,
    pageHeightPt: number,
    canvasWidth: number,
    canvasHeight: number,
): boolean {
    const c = pdfRectToCanvas(
        region,
        pageWidthPt,
        pageHeightPt,
        canvasWidth,
        canvasHeight,
    );

    return pt.x >= c.x && pt.x <= c.x + c.w && pt.y >= c.y && pt.y <= c.y + c.h;
}

function canvasRectsOverlap(
    a: { x: number; y: number; w: number; h: number },
    b: { x: number; y: number; w: number; h: number },
): boolean {
    return (
        a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y
    );
}

/** Regions whose canvas bounds overlap the selection rectangle (marquee). */
export function findRegionsInCanvasRect(
    regions: PdfTextRegion[],
    rect: { x: number; y: number; w: number; h: number },
    pageWidthPt: number,
    pageHeightPt: number,
    canvasWidth: number,
    canvasHeight: number,
): PdfTextRegion[] {
    if (rect.w < 1 || rect.h < 1) {
        return [];
    }

    return regions.filter((region) => {
        const c = pdfRectToCanvas(
            region,
            pageWidthPt,
            pageHeightPt,
            canvasWidth,
            canvasHeight,
        );

        return canvasRectsOverlap(c, rect);
    });
}

/** Prefer smallest region under the pointer (characters over words over lines). */
export function findRegionAtPoint(
    regions: PdfTextRegion[],
    pt: { x: number; y: number },
    pageWidthPt: number,
    pageHeightPt: number,
    canvasWidth: number,
    canvasHeight: number,
): PdfTextRegion | null {
    const hits = regions.filter((r) =>
        hitTestRegion(
            pt,
            r,
            pageWidthPt,
            pageHeightPt,
            canvasWidth,
            canvasHeight,
        ),
    );

    if (hits.length === 0) {
        return null;
    }

    hits.sort((a, b) => a.w * a.h - b.w * b.h);

    return hits[0] ?? null;
}
